import type { CoverageSummary, SavedView } from '@fruuit/shared'
import { COVERAGE_ENGAGEMENT_STATES } from '@fruuit/shared/contracts/coverage'
import { Button, Input, Select, type SelectOption } from '@fruuit/ui'
import { RotateCcw, Search } from 'lucide-react'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import type { FilterKey, FilterState } from './api'
import { countryLabel, sectorLabel, themeLabel } from './format'

export interface CoverageFiltersProps {
  filters: FilterState
  summary: CoverageSummary | undefined
  onChange: (next: FilterState) => void
  savedViews?: SavedView[]
  activeViewId?: string
  onChooseView?: (view: SavedView | null) => void
}

const STATE_LABELS: Record<string, string> = {
  active: 'Active',
  stalled: 'Stalled',
  closed: 'Closed',
  'not engaged': 'Not engaged',
}

const MAPPING_OPTIONS: SelectOption[] = [
  { value: 'exceptions', label: 'Mapping exceptions only' },
  { value: 'verified', label: 'Verified mappings' },
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'needs review', label: 'Needs review' },
  { value: 'out of scope', label: 'Out of scope' },
]

/**
 * Toolbar and filters (PORT-002/004): search, saved views, reset, and the
 * filter selects. Options come from the unfiltered data; every select has an
 * "All …" choice. Free text applies 450 ms after the last keystroke.
 */
export function CoverageFilters({
  filters,
  summary,
  onChange,
  savedViews,
  activeViewId,
  onChooseView,
}: CoverageFiltersProps) {
  const [query, setQuery] = useState(filters.query ?? '')
  const latest = useRef(filters)
  latest.current = filters

  useEffect(() => {
    setQuery(filters.query ?? '')
  }, [filters.query])

  useEffect(() => {
    if ((latest.current.query ?? '') === query) return
    const timer = setTimeout(() => onChange({ ...latest.current, query: query || undefined }), 450)
    return () => clearTimeout(timer)
  }, [query, onChange])

  const set = (key: FilterKey) => (event: ChangeEvent<HTMLSelectElement>) =>
    onChange({ ...filters, [key]: event.target.value || undefined })

  const options = summary?.options
  const select = (key: FilterKey, label: string, all: string, opts: SelectOption[]) => (
    <Select
      label={label}
      hideLabel
      value={filters[key] ?? ''}
      onChange={set(key)}
      placeholder={all}
      options={opts}
      className="min-w-0"
    />
  )
  const hasFilters = Object.values(filters).some(Boolean)

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-sage"
          />
          <Input
            label="Search portfolio"
            hideLabel
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search company, security or finding"
            inputClassName="pl-9"
          />
        </div>
        {savedViews && onChooseView ? (
          <Select
            label="Saved views"
            hideLabel
            value={activeViewId ?? ''}
            placeholder="Choose a saved view"
            options={savedViews.map((v) => ({ value: v.id, label: v.name }))}
            onChange={(event) =>
              onChooseView(savedViews.find((v) => v.id === event.target.value) ?? null)
            }
            className="lg:w-60"
          />
        ) : null}
        <Button
          variant="secondary"
          icon={<RotateCcw />}
          disabled={!hasFilters}
          onClick={() => onChange({})}
        >
          Reset filters
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {select(
          'portfolio',
          'Portfolio',
          'All portfolios',
          options?.portfolios.map((p) => ({ value: p.value, label: p.label })) ?? [],
        )}
        {select(
          'finding',
          'Finding',
          'All findings',
          options?.findings.map((f) => ({ value: f, label: f })) ?? [],
        )}
        {select(
          'theme',
          'Theme',
          'All themes',
          options?.themes.map((t) => ({ value: t, label: themeLabel(t) })) ?? [],
        )}
        {select(
          'sector',
          'Sector',
          'All sectors',
          options?.sectors.map((s) => ({ value: s, label: sectorLabel(s) })) ?? [],
        )}
        {select(
          'country',
          'Country',
          'All countries',
          options?.countries.map((c) => ({ value: c, label: countryLabel(c) })) ?? [],
        )}
        {select(
          'engagement_status',
          'Engagement state',
          'All engagement states',
          COVERAGE_ENGAGEMENT_STATES.map((s) => ({ value: s, label: STATE_LABELS[s] ?? s })),
        )}
        {select(
          'label',
          'Review status',
          'All review statuses',
          options?.labels.map((l) => ({ value: l, label: l })) ?? [],
        )}
        {select('mapping', 'Mapping', 'All mappings', MAPPING_OPTIONS)}
      </div>
    </div>
  )
}
